import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Lightbulb, RotateCcw } from "lucide-react";

const Q = [
  { q: "What is your main goal online?", opts: [
    { l: "Show my products / catalogue", tag: "catalogue" },
    { l: "Get calls & enquiries", tag: "leads" },
    { l: "Take bookings / appointments", tag: "booking" },
    { l: "Sell online", tag: "ecommerce" },
  ]},
  { q: "How do customers usually reach you?", opts: [
    { l: "WhatsApp & phone", tag: "whatsapp" },
    { l: "Walk-ins", tag: "location" },
    { l: "Social media", tag: "social" },
    { l: "Referrals", tag: "trust" },
  ]},
  { q: "How often will content change?", opts: [
    { l: "Rarely", tag: "static" },
    { l: "Monthly", tag: "cms" },
    { l: "Weekly / daily", tag: "cms" },
    { l: "Not sure", tag: "static" },
  ]},
];

function recommend(tags) {
  const recs = new Set(["Home", "About", "Contact with map"]);
  const feats = new Set(["Mobile-friendly design"]);
  if (tags.includes("catalogue")) { recs.add("Product / Service Catalogue"); recs.add("Gallery"); }
  if (tags.includes("leads") || tags.includes("whatsapp")) { feats.add("WhatsApp enquiry button"); feats.add("Lead capture form"); }
  if (tags.includes("booking")) { recs.add("Booking / Appointment page"); feats.add("Appointment booking"); }
  if (tags.includes("ecommerce")) { recs.add("Shop / Checkout"); feats.add("Online payments (once configured)"); }
  if (tags.includes("trust")) { recs.add("Testimonials (when approved)"); }
  if (tags.includes("social")) { feats.add("Social media links"); }
  if (tags.includes("cms")) { feats.add("Content management dashboard"); }
  return { pages: [...recs], features: [...feats] };
}

export default function IdeaFinder() {
  const [i, setI] = useState(0);
  const [tags, setTags] = useState([]);
  const [done, setDone] = useState(false);

  const pick = (tag) => {
    const nt = [...tags, tag];
    setTags(nt);
    if (i < Q.length - 1) setI(i + 1);
    else setDone(true);
  };
  const reset = () => { setI(0); setTags([]); setDone(false); };
  const result = done ? recommend(tags) : null;

  return (
    <div className="glass-strong rounded-3xl p-6 sm:p-10 max-w-3xl mx-auto" data-testid="idea-finder">
      <div className="flex items-center gap-3 mb-6">
        <span className="grid place-items-center w-11 h-11 rounded-xl bg-cyan-500/20 text-cyan-300"><Lightbulb className="w-5 h-5" /></span>
        <div>
          <h3 className="font-display text-xl font-bold text-white">Business Website Idea Finder</h3>
          <p className="text-sm text-slate-400">3 quick questions, transparent recommendations.</p>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {!done ? (
          <motion.div key={i} initial={{opacity:0,x:20}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-20}}>
            <div className="text-xs font-mono text-cyan-300 mb-2">Question {i+1} / {Q.length}</div>
            <h4 className="text-white text-lg font-semibold mb-5">{Q[i].q}</h4>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {Q[i].opts.map((o) => (
                <button key={o.l} data-testid={`idea-opt-${o.tag}`} onClick={()=>pick(o.tag)}
                  className="text-left px-4 py-3.5 rounded-xl border border-white/10 text-slate-200 hover:border-cyan-500/50 hover:bg-white/5 transition-all text-sm">{o.l}</button>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div key="res" initial={{opacity:0,scale:0.97}} animate={{opacity:1,scale:1}} data-testid="idea-result">
            <h4 className="text-white text-lg font-semibold mb-4">Recommended for your business</h4>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <div className="text-xs font-mono uppercase text-violet-300 mb-2">Suggested pages</div>
                <ul className="space-y-1.5">{result.pages.map((p)=>(<li key={p} className="text-sm text-slate-200 flex gap-2"><span className="text-violet-400">▸</span>{p}</li>))}</ul>
              </div>
              <div>
                <div className="text-xs font-mono uppercase text-cyan-300 mb-2">Useful features</div>
                <ul className="space-y-1.5">{result.features.map((p)=>(<li key={p} className="text-sm text-slate-200 flex gap-2"><span className="text-cyan-400">▸</span>{p}</li>))}</ul>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-5">These suggestions use simple transparent rules based on your answers — not an AI system.</p>
            <Button variant="outline" className="mt-5 border-white/15 bg-transparent text-white hover:bg-white/10" onClick={reset}><RotateCcw className="w-4 h-4 mr-2" />Start over</Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
